import React, {useRef, useState, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

// Utils
import useMountedState from "../hooks/useMountedState.js";

// Style
import {border} from "../style/components/variables.js";
import {voice} from "../style/components/typography.js";
import {Abbr, Inline} from "../style/components/general.js";

const Switch = ({
  testId = "switch",
  state = false,
  disabled = false,
  on = "ON",
  off = "OFF",
  showValue = true,
  callback,
  onColor,
  offColor,
  syncState = false
}) => {
  const isMounted = useMountedState();

  const element = useRef(null);

  const [enabled, setEnabled] = useState(state);

  const setCheck = () => {
    if (!disabled) {
      if (!syncState) {
        const {checked} = element.current;
        element.current.checked = !checked;
        setEnabled(!state);
      }
      callback(!state);
    }
  };

  // set intial state
  useEffect(() => {
    if (isMounted() && element.current) {
      element.current.checked = state;
      setEnabled(state);
    }
  }, [isMounted, state]);

  const color = useMemo(() => (state ? onColor : offColor), [state, onColor, offColor]);

  const stateName = useMemo(() => (enabled ? on : off), [enabled, on, off]);

  const onLabel = on.toUpperCase();
  const offLabel = off.toUpperCase();

  return (
    <Inline>
      <SwitchWrapper type="button" onClick={setCheck} disabled={disabled} data-testid={testId}>
        <Abbr title={!showValue ? stateName : undefined}>
          <SwitchOutline checked={enabled} color={color} disabled={disabled}>
            <CheckInput
              type="checkbox"
              ref={element}
              data-testid={`${testId}-checkbox`}
              disabled={disabled}
              defaultChecked={enabled}
            />
            <SwitchIcon checked={enabled} color={color} />
          </SwitchOutline>
        </Abbr>
      </SwitchWrapper>
      {showValue && (
        <SwitchLabel color={color}>
          <SwitchLabelSizer aria-hidden="true">{onLabel}</SwitchLabelSizer>
          <SwitchLabelSizer aria-hidden="true">{offLabel}</SwitchLabelSizer>
          <SwitchLabelText>{enabled ? onLabel : offLabel}</SwitchLabelText>
        </SwitchLabel>
      )}
    </Inline>
  );
};

Switch.propTypes = {
  testId: PropTypes.string,
  state: PropTypes.bool,
  disabled: PropTypes.bool,
  on: PropTypes.string,
  off: PropTypes.string,
  showValue: PropTypes.bool,
  callback: PropTypes.func.isRequired,
  onColor: PropTypes.string,
  offColor: PropTypes.string,
  syncState: PropTypes.bool
};

// Style Overrides

const SwitchWrapper = styled.button`
  display: flex;

  ${props =>
    props.disabled &&
    css`
      &:hover {
        cursor: not-allowed;
      }
    `}
`;

const SwitchOutline = styled.div`
  position: relative;
  width: 30px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${border} solid ${({color, theme}) => color || theme.secondary};
  border-radius: 10px;
  background: ${({color, checked, theme}) => {
    if (color) return "transparent";
    return !checked ? theme.secondary : "transparent";
  }};
  transition: all ease 0.1s;

  &:hover {
    cursor: pointer;
  }

  ${props =>
    props.disabled &&
    css`
      &:hover {
        cursor: inherit;
      }
    `}
`;

const SwitchIcon = styled.span`
  width: 100%;
  height: 100%;
  cursor: inherit;

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 10px;
    height: 10px;
    background: ${({theme, color, checked}) =>
      color || (!checked ? theme.tertiary : theme.secondary)};
    border-radius: 50%;
    margin: 2px;
    transition: all ease 0.1s;
  }
`;

const CheckInput = styled.input`
  display: none;

  &:checked + span:after {
    left: 12px;
  }
`;

const SwitchLabel = styled.span`
  display: inline-grid;
  flex: 0 0 auto;
  width: max-content;
  ${voice.normal};
  color: ${({color, theme}) => color || theme.secondary};
`;

const SwitchLabelSizer = styled.span`
  grid-column: 1;
  height: 0;
  min-height: 0;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
`;

const SwitchLabelText = styled.span`
  grid-column: 1;
  grid-row: 1;
  white-space: nowrap;
`;

export default Switch;
